<script setup lang="ts">
import { useCourseExemptionMutations } from '@/composables/mutations/tertiary/useCourseExemptionMutations';
import { useCourses } from '@/composables/queries/useCourses';
import { formatCourseInstance } from '@/utils/courseInstance';
import { downloadExemptionTemplate } from '@/utils/exemptionTemplate';

const emit = defineEmits<{ success: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const { bulkExemptFromExcel, bulkExemptingFromExcel } = useCourseExemptionMutations();

// Courses — load initial set, search server-side on filter
const { courses, setFilter: setCourseFilter } = useCourses({ autoFetch: true, first: 100, page: 1 });
const courseOptions = computed(() =>
  courses.value.map((c: { id: string; course_code: string; course_title: string; unit: number; course_instance_id?: string; course_instance_year?: number | null }) => ({
    label: `${c.course_code} — ${c.course_title} (${c.unit} units)`,
    value: c.id,
    code: c.course_code,
    title: c.course_title,
    unit: c.unit,
    instance: formatCourseInstance(c),
  })),
);

let courseDebounce: ReturnType<typeof setTimeout> | null = null;
function onCourseFilter(event: { originalEvent: Event; value: string }) {
  if (courseDebounce) clearTimeout(courseDebounce);
  courseDebounce = setTimeout(() => {
    setCourseFilter({ query: event.value });
  }, 300);
}

// Form state
const selectedFile = ref<File | null>(null);
const selectedCourses = ref<string[]>([]);

function onFileSelect(event: { files: File[] }) {
  selectedFile.value = event.files[0] ?? null;
}

function onFileClear() {
  selectedFile.value = null;
}

async function handleSubmit() {
  if (!selectedFile.value || !selectedCourses.value.length) return;
  const result = await bulkExemptFromExcel(selectedFile.value, selectedCourses.value);
  if (result?.success) {
    resetForm();
    visible.value = false;
    emit('success');
  }
}

function resetForm() {
  selectedFile.value = null;
  selectedCourses.value = [];
}

function onHide() {
  resetForm();
}
</script>

<template>
  <Dialog data-testid="bulk-excel-dialog" v-model:visible="visible" header="Import Exemptions from Excel" modal :style="{ width: '36rem' }" @hide="onHide">
    <div class="flex flex-col gap-4 pt-2">
      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between gap-2">
          <label class="text-sm font-medium text-gray-700">Matric Number List (Excel/CSV)</label>
          <Button
            data-testid="bulk-excel-download-sample"
            label="Download sample"
            icon="pi pi-download"
            size="small"
            text
            @click="downloadExemptionTemplate()"
          />
        </div>
        <FileUpload
          data-testid="bulk-excel-file-upload"
          mode="basic"
          accept=".xlsx,.xls,.csv"
          :maxFileSize="5000000"
          chooseLabel="Choose File"
          @select="onFileSelect"
          @clear="onFileClear"
          class="w-full"
        />
        <small class="text-gray-500">
          Upload a spreadsheet with a <code>matric_no</code> column (and optional <code>deleted</code>). Download the sample to see the exact format.
        </small>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Courses to Exempt</label>
        <MultiSelect
          data-testid="bulk-excel-course-select"
          v-model="selectedCourses"
          :options="courseOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Search courses..."
          filter
          @filter="onCourseFilter"
          :maxSelectedLabels="3"
          class="w-full"
        >
          <template #option="{ option }">
            <div class="flex flex-col py-0.5">
              <span class="text-sm font-medium">{{ option.code }} — {{ option.title }}</span>
              <span class="text-xs text-gray-400">Unit: {{ option.unit ?? '—' }} · {{ option.instance || '—' }}</span>
            </div>
          </template>
        </MultiSelect>
        <small class="text-gray-500">{{ selectedCourses.length }} course(s) selected</small>
      </div>
    </div>

    <template #footer>
      <Button size="small" data-testid="bulk-excel-cancel" label="Cancel" severity="secondary" text @click="visible = false" />
      <Button size="small"
        data-testid="bulk-excel-submit"
        label="Import & Exempt"
        icon="pi pi-upload"
        @click="handleSubmit"
        :loading="bulkExemptingFromExcel"
        :disabled="!selectedFile || !selectedCourses.length"
      />
    </template>
  </Dialog>
</template>
